// src/hooks/useProjectedProfile.js

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { playerSessionService } from '../services/playerSessionService'
import { getProfileConfig } from '../data/projected-profiles'

/**
 * Determine archetype from session responses by counting profile_signal tags
 * from decision-type beats. Falls back to 'stabilizer' if no signals found.
 */
function resolveArchetype(session, beats) {
  if (!session?.responses || !beats) return 'stabilizer'

  const signalCounts = {}

  session.responses.forEach((response) => {
    if (response.beat_type !== 'decision' && response.beat_type !== 'choice') return

    const beat = beats.find((b) => b.id === response.beat_id)
    if (!beat?.options?.choices) return

    const selectedId = response.response_data?.choice_id || response.response_data
    const selectedChoice = beat.options.choices.find((c) => c.id === selectedId)
    if (!selectedChoice?.profile_signal) return

    const signal = selectedChoice.profile_signal
    signalCounts[signal] = (signalCounts[signal] || 0) + 1
  })

  if (Object.keys(signalCounts).length === 0) return 'stabilizer'

  return Object.entries(signalCounts).sort((a, b) => b[1] - a[1])[0][0]
}

/**
 * Extract learner inputs (free-text responses from input beats) for display.
 */
function extractInputs(session, beats) {
  if (!session?.responses || !beats) return []

  return session.responses
    .filter((r) => r.beat_type === 'input')
    .map((r) => {
      const beat = beats.find((b) => b.id === r.beat_id)
      return {
        beatName: beat?.name || r.beat_name || 'Response',
        question: beat?.content || '',
        answer: typeof r.response_data === 'string' ? r.response_data : '',
      }
    })
    .filter((input) => input.answer)
}

/**
 * Extract decision choices for display.
 */
function extractDecisions(session, beats) {
  if (!session?.responses || !beats) return []

  return beats
    .filter((b) => b.options?.beat_type === 'decision')
    .map((beat) => {
      const response = session.responses.find((r) => r.beat_id === beat.id)
      if (!response) return null

      const selectedId = response.response_data?.choice_id || response.response_data
      const selectedChoice = beat.options.choices?.find((c) => c.id === selectedId)

      return {
        beatName: beat.name,
        question: beat.content,
        answer: selectedChoice?.text || selectedId || 'Selected',
      }
    })
    .filter(Boolean)
}

/**
 * Hook that assembles all data needed for the Projected Learning Profile page.
 *
 * @param {string} sessionId - Player session UUID
 * @param {Object} story - Story object (from useGameSession or fetched separately)
 * @param {Array} beats - Beat definitions for the story thread
 * @returns {{ profile, isLoading, error, hasConfig }}
 */
export function useProjectedProfile(sessionId, story, beats) {
  const {
    data: session,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['playerSession', sessionId],
    queryFn: () => playerSessionService.getPlayerSession(sessionId),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
  })

  const profile = useMemo(() => {
    if (!session || !story || !beats) return null

    const config = getProfileConfig(story.id)
    if (!config) return null

    const archetypeKey = resolveArchetype(session, beats)
    const archetype = config.archetypes[archetypeKey]
    if (!archetype) return null

    return {
      programName: config.programName,
      dimensionLabels: config.dimensionLabels,
      archetype: { key: archetypeKey, ...archetype },
      sprint1: {
        label: config.sprint1Label,
        weeks: config.sprint1Weeks,
        deliverables: config.sprint1Deliverables,
        scores: archetype.week12Scores,
        narrative: archetype.week12Narrative,
      },
      sprint2: {
        label: config.sprint2Label,
        weeks: config.sprint2Weeks,
        deliverables: config.sprint2Deliverables,
        scores: archetype.week24Scores,
        narrative: archetype.week24Narrative,
      },
      skillTrajectory: config.skillTrajectory[archetypeKey] || [],
      learnerInputs: extractInputs(session, beats),
      learnerDecisions: extractDecisions(session, beats),
      completedAt: session.completed_at,
      sessionDuration: session.completed_at && session.started_at
        ? Math.round((new Date(session.completed_at) - new Date(session.started_at)) / 60000)
        : null,
    }
  }, [session, story, beats])

  const hasConfig = !!getProfileConfig(story?.id)

  return { profile, isLoading, error, hasConfig }
}
